/**
 * useChargeDefaults — hook fino que expõe os padrões de cobrança (juros/multa
 * e prazo de expiração) configurados em Configurações → Financeiro para
 * pré-preencher os modais de criação de cobrança (avulsa, parcelada e
 * recorrência).
 *
 * IMPORTANTE: Os defaults são aplicados APENAS no frontend (prefill). O
 * backend não consome esses valores — cada cobrança é salva exatamente com o
 * que foi enviado no payload.
 *
 * Reaproveita useFinancialSettings (mesma query/cache), não dispara
 * requisições adicionais.
 *
 * RELEASE: 2026-06-20 v1.0.0 - Versão inicial.
 */
import { useFinancialSettings } from '@/hooks/useFinancialSettings';
import {
  JUROS_DEFAULT,
  type JurosValue,
} from '@/components/financial/subscription/SubscriptionJurosSection';

export interface ChargeDefaults {
  /** Indica se o usuário marcou "aplicar juros/multa por padrão" */
  jurosEnabled: boolean;
  /** Valores de juros/multa configurados (já normalizados em JurosValue) */
  juros: JurosValue;
  /** Indica se o usuário marcou "definir prazo de expiração padrão" */
  expiracaoEnabled: boolean;
  /** Dias de expiração pós-vencimento configurados (default 60) */
  diasExpiracao: number;
  /** Loading do hook subjacente */
  isLoading: boolean;
}

/**
 * Hook de leitura dos padrões de cobrança do tenant.
 * Usa o mesmo cache do useFinancialSettings — sem requisições adicionais.
 */
export function useChargeDefaults(): ChargeDefaults {
  const { config, isLoading } = useFinancialSettings();

  const jurosEnabled = !!config?.charge_juros_enabled;
  const expiracaoEnabled = !!config?.charge_expiracao_enabled;

  const rawJuros = config?.charge_juros ?? {};
  const juros: JurosValue = {
    enabled: jurosEnabled,
    multa_tipo: (rawJuros.multa_tipo as 'percent' | 'fixed') || 'percent',
    multa_valor: Number(rawJuros.multa_valor) || 0,
    juros_tipo: (rawJuros.juros_tipo as 'percent' | 'fixed') || 'percent',
    juros_valor: Number(rawJuros.juros_valor) || 0,
  };

  const diasExpiracao = Number(config?.charge_dias_expiracao) || 60;

  return {
    jurosEnabled,
    juros: jurosEnabled ? juros : JUROS_DEFAULT,
    expiracaoEnabled,
    diasExpiracao,
    isLoading,
  };
}

export default useChargeDefaults;
